import { mediaUrl } from "@/lib/api";
import { Surface, Pill, AButton } from "@/components/ios";
import {
  Camera, Check, X, Minus, Sparkles, Mic, MicOff, Loader2, Sparkle,
  ShieldCheck, ImagePlus, Aperture, Plus, Trash2,
} from "lucide-react";

const RATING_LABEL = { clean: "Clean", undamaged: "Undamaged", working: "Working" };
export const thumbSrc = (m) => m.data_url || mediaUrl(m.signed_url);

const SCENE_LABEL = {
  room_overview: "Overview", wall: "Wall", ceiling: "Ceiling", floor: "Floor",
  window: "Window", door: "Door", fixture_closeup: "Close-up", exterior: "Exterior", other: "Detail",
};

export function TriToggle({ field, value, onChange }) {
  const next = value === undefined ? true : value === true ? false : undefined;
  const bg = value === true ? "var(--success)" : value === false ? "var(--danger)" : "var(--surface-2)";
  const color = value === undefined ? "var(--text-secondary)" : "#fff";
  const Icon = value === true ? Check : value === false ? X : Minus;
  return (
    <button
      data-testid={`rating-${field}`}
      onClick={() => onChange(next)}
      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold transition-colors border"
      style={{ background: bg, color, borderColor: value === undefined ? "var(--border)" : bg }}
    >
      <Icon size={13} /> {RATING_LABEL[field]}
    </button>
  );
}

export function RoomOverview({ roomOverview, section, assignMedia }) {
  if (!roomOverview.length) return null;
  return (
    <Surface className="p-4" data-testid="room-overview-section">
      <div className="flex items-center gap-2 text-sm font-semibold mb-1" style={{ color: "var(--text)" }}>
        <Aperture size={15} className="accent-text" /> Room overview · {roomOverview.length}
      </div>
      <p className="text-xs text-secondary mb-3">Wide establishing shots of {section.title}. These stay as room context (not tied to a single item). Mis-tagged? Attach one to an item below.</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {roomOverview.map((m) => (
          <div key={m.id} className="space-y-1.5" data-testid={`overview-photo-${m.id}`}>
            <div className="relative">
              <img src={thumbSrc(m)} alt="room overview" className="w-full h-24 rounded-lg object-cover" />
              <span className="absolute top-1 left-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white" style={{ background: "var(--accent)" }}>
                <Aperture size={8} /> Overview
              </span>
              {m.redacted && <span className="absolute top-1 right-1 h-4 w-4 rounded-full grid place-items-center text-white" style={{ background: "var(--accent)" }}><ShieldCheck size={9} /></span>}
            </div>
            <select data-testid={`overview-assign-${m.id}`} defaultValue="" onChange={(e) => assignMedia(m.id, e.target.value)}
              className="w-full text-xs rounded-lg px-2 py-1.5 outline-none border"
              style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }}>
              <option value="" disabled>Keep as overview · or attach to item…</option>
              {section.items.map((it) => <option key={it.key} value={it.key}>{it.label}</option>)}
            </select>
          </div>
        ))}
      </div>
    </Surface>
  );
}

export function RoomEvidencePool({ roomUnassigned, section, matching, autoMatch,
  acceptCandidate, dismissCandidate, assignMedia }) {
  if (!roomUnassigned.length) return null;
  return (
    <Surface className="p-4" data-testid="room-evidence-pool">
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <div className="flex items-center gap-2 text-sm font-semibold" style={{ color: "var(--text)" }}>
          <Camera size={15} className="accent-text" /> Room evidence · {roomUnassigned.length}
        </div>
        <AButton variant="text" testid="auto-match-btn" disabled={matching} onClick={autoMatch}>
          {matching ? <Loader2 size={14} className="inline mr-1 animate-spin" /> : <Sparkles size={14} className="inline mr-1" />}
          {matching ? "Matching…" : "Auto-match with AI"}
        </AButton>
      </div>
      <p className="text-xs text-secondary mb-3">Photos captured in this area. AI auto-matches them to items when online; assign or correct any below (you always have the final say).</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {roomUnassigned.map((m) => (
          <div key={m.id} className="space-y-1.5" data-testid={`pool-photo-${m.id}`}>
            <div className="relative">
              <img src={thumbSrc(m)} alt="evidence" className="w-full h-24 rounded-lg object-cover" />
              {m.scene_type && m.scene_type !== "fixture_closeup" && (
                <span className="absolute bottom-1 left-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold text-white bg-black/55 backdrop-blur">{SCENE_LABEL[m.scene_type] || "Detail"}</span>
              )}
              {m.redacted && <span className="absolute top-1 left-1 h-4 w-4 rounded-full grid place-items-center text-white" style={{ background: "var(--accent)" }}><ShieldCheck size={9} /></span>}
            </div>
            {m.ai_candidate ? (
              <div className="rounded-lg p-2 border" style={{ borderColor: "var(--accent)", background: "color-mix(in srgb, var(--accent) 8%, transparent)" }} data-testid={`candidate-${m.id}`}>
                <div className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: "var(--text)" }}>
                  <Sparkles size={11} className="accent-text" /> AI thinks: {m.ai_candidate.label}
                </div>
                {m.ai_candidate.subject && (
                  <div className="text-[10px] text-secondary line-clamp-2 mt-0.5">Saw: {m.ai_candidate.subject}</div>
                )}
                <div className="flex gap-1.5 mt-1.5">
                  <button data-testid={`accept-candidate-${m.id}`} onClick={() => acceptCandidate(m)}
                    className="flex-1 text-[11px] font-semibold py-1 rounded-md text-white" style={{ background: "var(--accent)" }}>Confirm</button>
                  <button data-testid={`dismiss-candidate-${m.id}`} onClick={() => dismissCandidate(m)}
                    className="flex-1 text-[11px] font-semibold py-1 rounded-md border" style={{ borderColor: "var(--border)", color: "var(--text-secondary)" }}>Pick another</button>
                </div>
              </div>
            ) : (
              <select data-testid={`assign-${m.id}`} defaultValue="" onChange={(e) => assignMedia(m.id, e.target.value)}
                className="w-full text-xs rounded-lg px-2 py-1.5 outline-none border"
                style={{ background: "var(--surface-2)", borderColor: "var(--border)", color: "var(--text)" }}>
                <option value="" disabled>Attach to item…</option>
                {section.items.map((it) => <option key={it.key} value={it.key}>{it.label}</option>)}
              </select>
            )}
          </div>
        ))}
      </div>
    </Surface>
  );
}

export function ItemCard({ it, r, itemMedia, room, dict, setItem, setCam, onPickFile, unassignMedia, aiCheck, assignMedia }) {
  const listening = dict.listening === it.key;
  return (
    <Surface className="p-4" data-testid={`item-${it.key}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="font-medium" style={{ color: "var(--text)" }}>{it.label}</div>
        <div className="flex items-center gap-3 shrink-0">
          <button data-testid={`camera-photo-${it.key}`} onClick={() => setCam({ key: it.key, room, label: it.label })}
            className="flex items-center gap-1 text-xs font-semibold accent-text"><Camera size={14} /> Camera</button>
          <button data-testid={`upload-photo-${it.key}`} onClick={() => onPickFile(it.key, room)}
            className="flex items-center gap-1 text-xs font-semibold text-secondary"><ImagePlus size={14} /> Upload</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {it.rating_fields.map((f) => (
          <TriToggle key={f} field={f} value={r[f]} onChange={(v) => setItem(it.key, room, { [f]: v })} />
        ))}
      </div>
      {itemMedia.length > 0 && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {itemMedia.map((m) => (
            <div key={m.id} className="relative group" data-testid={`item-photo-${m.id}`}>
              <img src={thumbSrc(m)} className="h-16 w-16 rounded-lg object-cover" alt="evidence" />
              {m.redacted && <span className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full grid place-items-center text-white" style={{ background: "var(--accent)" }} title="PII redacted"><ShieldCheck size={9} /></span>}
              {m.auto_assigned && (
                <span className="absolute top-0.5 right-0.5 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold leading-none text-white [&_svg]:shrink-0" style={{ background: m.assigned_by === "voice" ? "var(--accent)" : "var(--warning)" }} title={m.ai_item_reason || (m.assigned_by === "voice" ? "Linked from your spoken commentary" : "AI-matched")} data-testid={`ai-badge-${m.id}`}>
                  {m.assigned_by === "voice" ? <><Mic size={8} /> Voice</> : <><Sparkles size={8} /> AI</>}
                </span>
              )}
              {m.room && (
                <button onClick={() => unassignMedia(m.id)} data-testid={`detach-${m.id}`} title="Wrong item? Send back to room evidence"
                  className="absolute -top-1.5 -left-1.5 h-5 w-5 rounded-full grid place-items-center text-white opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: "var(--danger)" }}>
                  <X size={11} />
                </button>
              )}
              <button onClick={() => aiCheck(it.key, room, m.id)} data-testid={`ai-check-${it.key}-${m.id}`}
                className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full grid place-items-center text-white" style={{ background: "var(--accent)" }} title="AI analyse (all photos on this item)">
                <Sparkle size={12} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <input value={r.note || ""} onChange={(e) => setItem(it.key, room, { note: e.target.value, ai_generated: false })}
          placeholder={listening ? "Listening…" : "Add a comment (or dictate)…"} data-testid={`note-${it.key}`}
          className="flex-1 min-w-0 text-sm rounded-lg px-3 py-2 outline-none border"
          style={{ background: "var(--surface-2)", borderColor: listening ? "var(--accent)" : "var(--border)", color: "var(--text)" }} />
        {dict.supported && (
          <button data-testid={`dictate-${it.key}`} onClick={() => { listening ? dict.stop() : dict.start(it.key, r.note || "", (txt) => setItem(it.key, room, { note: txt }, true)); }}
            className="h-9 w-9 rounded-lg grid place-items-center shrink-0 border transition-colors"
            style={{ borderColor: listening ? "var(--accent)" : "var(--border)", background: listening ? "var(--accent)" : "var(--surface-2)", color: listening ? "#fff" : "var(--text-secondary)" }}>
            {listening ? <MicOff size={15} /> : <Mic size={15} />}
          </button>
        )}
      </div>
    </Surface>
  );
}

export function CustomItemCard({ ckey, cr, itemMedia, room, dict, setItem, setCam, removeCustomItem }) {
  const listening = dict.listening === ckey;
  return (
    <Surface className="p-4" style={{ borderColor: "var(--warning)" }} data-testid={`custom-item-${ckey}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="font-medium" style={{ color: "var(--text)" }}>{cr.label}</span>
          <Pill tone="warning"><Sparkles size={10} /> Added on-site</Pill>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <button data-testid={`camera-photo-${ckey}`} onClick={() => setCam({ key: ckey, room, label: cr.label })}
            className="flex items-center gap-1 text-xs font-semibold accent-text"><Camera size={14} /> Camera</button>
          <button data-testid={`remove-item-${ckey}`} onClick={() => removeCustomItem(ckey)}
            className="flex items-center gap-1 text-xs font-semibold" style={{ color: "var(--danger)" }}><Trash2 size={13} /> Remove</button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-3">
        {["clean", "undamaged", "working"].map((f) => (
          <TriToggle key={f} field={f} value={cr[f]} onChange={(v) => setItem(ckey, room, { [f]: v })} />
        ))}
      </div>
      {itemMedia.length > 0 && (
        <div className="flex gap-2 mt-3 flex-wrap">
          {itemMedia.map((m) => (
            <div key={m.id} className="relative" data-testid={`item-photo-${m.id}`}>
              <img src={thumbSrc(m)} className="h-16 w-16 rounded-lg object-cover" alt="evidence" />
              {m.redacted && <span className="absolute top-0.5 left-0.5 h-4 w-4 rounded-full grid place-items-center text-white" style={{ background: "var(--accent)" }}><ShieldCheck size={9} /></span>}
            </div>
          ))}
        </div>
      )}
      <div className="mt-3 flex items-center gap-2">
        <input value={cr.note || ""} onChange={(e) => setItem(ckey, room, { note: e.target.value, ai_generated: false })}
          placeholder={listening ? "Listening…" : "Describe the condition…"} data-testid={`note-${ckey}`}
          className="flex-1 min-w-0 text-sm rounded-lg px-3 py-2 outline-none border"
          style={{ background: "var(--surface-2)", borderColor: listening ? "var(--accent)" : "var(--border)", color: "var(--text)" }} />
        {dict.supported && (
          <button data-testid={`dictate-${ckey}`} onClick={() => { listening ? dict.stop() : dict.start(ckey, cr.note || "", (txt) => setItem(ckey, room, { note: txt }, true)); }}
            className="h-9 w-9 rounded-lg grid place-items-center shrink-0 border transition-colors"
            style={{ borderColor: listening ? "var(--accent)" : "var(--border)", background: listening ? "var(--accent)" : "var(--surface-2)", color: listening ? "#fff" : "var(--text-secondary)" }}>
            {listening ? <MicOff size={15} /> : <Mic size={15} />}
          </button>
        )}
      </div>
    </Surface>
  );
}
